/*
  Markdown container to render markdown articles
*/

import React from "react";
import { Title, Text, Link, LinkExternal, Picture, Icon } from "../../atoms";
import { VideoPlayer, Carousel } from "./../index";
import { lighten } from "polished";
import PropTypes from "prop-types";
import Markdown from "markdown-to-jsx";
import styled from "styled-components";
import { rem } from "polished";
import { isIE } from "./../../../utils/utilities";

const MarkdownRenderer = ({
  content,
  activeColor,
  isInline,
  isThemeReverse,
  className,

  ...props
}) => {
  const imageOption = props.default
    ? {}
    : {
        img: {
          component: Picture,
          props: {
            size: "md"
          }
        }
      };
  const markdownOptions = {
    forceBlock: true,
    overrides: {
      icon: {
        component: Icon
      },
      picture: {
        component: Picture,
        props: {
          size: "md"
        }
      },
      video: {
        component: isIE() ? null : VideoPlayer
      },
      link: {
        component: LinkExternal,
        props: {
          activeColor
        }
      },
      h1: {
        component: Header,
        props: {
          as: "h1"
        }
      },
      h2: {
        component: Header,
        props: {
          as: "h2"
        }
      },
      h3: {
        component: Header,
        props: {
          as: "h3"
        }
      },
      h4: {
        component: Header,
        props: {
          as: "h4"
        }
      },
      h5: {
        component: Header,
        props: {
          as: "h5"
        }
      },
      p: {
        component: Text,
        props: {
          color: "#000",
          fontWeight: "300"
        }
      },
      a: {
        component: Link,
        props: {
          fontWeight: "400",
          fontSize: "14px",
          paddingBottom: "5px",
          native: true
        }
      },
      carousel: {
        component: Carousel,
        props: {
          hasVerticalSpacing: true
        }
      },
      carouselxl: {
        component: Carousel,
        props: {
          hasVerticalSpacing: false
        }
      },
      ...imageOption
    }
  };

  return (
    <MarkdownStyles
      activeColor={activeColor}
      isInline={isInline}
      isThemeReverse={isThemeReverse}
      className={className}
      {...props}
    >
      <Markdown className="markdown" options={markdownOptions}>
        {content}
      </Markdown>
    </MarkdownStyles>
  );
};

MarkdownRenderer.propTypes = {
  content: PropTypes.string.isRequired,
  activeColor: PropTypes.string,
  isInline: PropTypes.bool,
  isThemeReverse: PropTypes.bool,
  className: PropTypes.string,
  default: PropTypes.bool
};

MarkdownRenderer.defaultProps = {
  activeColor: "#00b2bd",
  isInline: false,
  isThemeReverse: false,
  className: "",
  default: false
};

// add markdown styles below
export const MarkdownStyles = styled.div`
  ${props => props.isInline && `display: inline-block;`}

  & .slick-slider:first-child {
    padding-top: 0;
  }

  .markdown {
    h2:first-child {
      padding-top: 0;
    }
    h3:first-child {
      padding-top: 0;
    }
    img:first-child {
      padding-top: 0;
    }
    picture:first-child {
      img {
        padding-top: 0;
      }
      }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: ${props =>
        props.isThemeReverse
          ? props.theme.components.text.colorReverse
          : props.theme.components.text.color};
      font-weight: ${props => props.theme.weight.xbase};
      padding-bottom: ${props => props.theme.spacing.xs3};
      font-size: ${props => props.theme.sizes.s};
      display: flex;
      flex-direction: column;
      line-height: ${props => props.theme.components.title.lineHeight};

      a {
        &.link-external {
          padding-bottom: 5px;
        }

        font-size: inherit;
        font-weight: inherit;
        text-decoration: none;
        &:hover {
          color: ${props =>
            props.isThemeReverse
              ? props.theme.components.text.colorReverse
              : props.theme.components.text.color};
          &:after {
            content: "";
            width: ${props =>
              props.theme.components.line.width +
              props.theme.components.line.widthDifference}px;
          }
        }
        &:after {
          content: "";
          transition: width 0.2s;
          display: block;
          margin-top: ${props => props.theme.components.line.marginTopMarkdown}px;
          color: ${props => props.theme.components.line.color};
          margin-bottom: 0;
          width: ${props => props.theme.components.line.width}px;
          height: ${props => props.theme.components.line.height}px;
          background-color: ${props => props.theme.colors.black};
        }
      }
    }

    h1, h1 {
      font-size: ${props => props.theme.sizes.xl3m};
      font-weight: ${props => props.theme.weight.base};
      padding-top: ${props => props.theme.spacing.xl2};
      padding-bottom: ${props => props.theme.spacing.xs2};
      a {
        font-size: inherit;
        font-weight: inherit;
        &:hover {
          &:after {
            width: ${props =>
              props.theme.components.line.width +
              props.theme.components.line.widthDifference}px;
          }
        }
        &:after {
          width: ${props => props.theme.components.line.width}px;
          height: ${props => props.theme.components.line.height}px;
        }
      }
    }

    h2, h2 {
      color: ${props => props.activeColor};
      font-size: ${props => props.theme.sizes.xl2};
      padding-top: ${props => props.theme.spacing.xl};
      padding-bottom: ${props => props.theme.spacing.xs2};
      a {
        color: ${props =>
          props.activeColor} !important;
        &:after {
          content: "";
          width: ${props =>
            props.theme.components.line.width -
            props.theme.components.line.widthDifference}px;
          background-color: ${props =>
            props.activeColor};
          height: ${props => props.theme.components.line.height - 1}px;
          margin-top: ${props =>
            props.theme.components.line.marginTopMarkdown - 4}px;
        }
        &:hover {
          text-decoration: none;
          &:after {
            content: "";
            width: ${props => props.theme.components.line.width}px;
          }
        }
      }
    }

    h3, h3  {
      padding-top: ${props => props.theme.spacing.s};
      font-size: ${props => props.theme.sizes.l};
      a {
        &:after {
          content: "";
          height: ${props => props.theme.components.line.height - 2}px;
          width: ${props =>
            props.theme.components.line.width -
            props.theme.components.line.widthDifference * 1.2}px;
          margin-top: ${props =>
            props.theme.components.line.marginTopMarkdown - 4}px;
        }
        &:hover {
          &:after {
            content: "";
            width: ${props =>
              props.theme.components.line.width -
              props.theme.components.line.widthDifference +
              4}px;
          }
        }
      }
    }

    h4, h4 {
      padding-top: ${props => props.theme.spacing.s};
      font-size: ${props => props.theme.sizes.m};
      a {
        &:after {
          content: "";
          height: ${props => props.theme.components.line.height - 2}px;
          width: ${props =>
            props.theme.components.line.width -
            props.theme.components.line.widthDifference * 1.5}px;
          margin-top: ${props =>
            props.theme.components.line.marginTopMarkdown - 4}px;
        }
        &:hover {
          &:after {
            content: "";
            width: ${props =>
              props.theme.components.line.width -
              props.theme.components.line.widthDifference +
              2}px;
          }
        }
      }
    }

    h5 {
      padding-top: ${props => props.theme.spacing.s};
      font-size: ${props => props.theme.sizes.s};
      a {
        &:after {
          content: "";
          height: ${props => props.theme.components.line.height - 2}px;
          width: ${props =>
            props.theme.components.line.width -
            props.theme.components.line.widthDifference * 2}px;
          margin-top: ${props =>
            props.theme.components.line.marginTopMarkdown - 4}px;
        }
        &:hover {
          &:after {
            content: "";
            width: ${props =>
              props.theme.components.line.width -
              props.theme.components.line.widthDifference}px;
          }
        }
      }
    }

    p {
      line-height: 1.6;
      padding: 0;
      padding-bottom: ${props => props.theme.spacing.s};
      text-transform: none;
      font-size: ${rem("15px")};
      font-weight: ${props => props.theme.weight.light};
      letter-spacing: ${props => props.theme.values.letterSpacing};
      font-family: ${props => props.theme.type.base};
      color: ${props =>
        props.isThemeReverse
          ? props.theme.components.text.colorReverse
    : props.theme.components.text.color};
      font-weight: ${props => props.theme.weight.light};
    }

    hr {
      margin-bottom: ${props => props.theme.spacing.m};
      margin-top: ${props => props.theme.spacing.m};
      background-color: ${props =>
        props.theme.colors.greyBorder};
      height: 1px;
      border: none;
    }

    ul,
    ol {
      font-size: ${props => props.theme.sizes.s};
      line-height: 1.6;
      list-style-position: outside;
      font-weight: ${props => props.theme.weight.light};
      padding-bottom: ${props => props.theme.components.ListItem.spacingBottom};
      transform: translateX(10px);
      letter-spacing: ${props => props.theme.values.letterSpacing};
      color: ${props =>
        props.isThemeReverse
          ? props.theme.components.text.colorReverse
          : props.theme.components.text.color};
    }
    ol {
      transform: translateX(15px);
    }
    ul > li {
      list-style-type: none;
      position: relative;
      &::before {
        display: inline-block;
        vertical-align: middle;
        width: 2px;
        height: 2px;
        background-color: ${props =>
          props.isThemeReverse
            ? props.theme.colors.white
            : props.theme.colors.blackLight};
        border-radius: 50%;
        transform: translateX(-10px);
        content: "";
        position: absolute;
        top: 11px;
      }
      p {
        display: inline;
      }
    }

    a {
      font-weight: ${props => props.theme.weight.light};
      text-decoration: underline;
      text-decoration-color: ${props =>
        props.theme.colors.black};
      color: ${props =>
        props.theme.colors.black};
      &.white {
        text-decoration-color: ${props =>
          props.theme.colors.white};
        color: ${props =>
          props.theme.colors.white};
        &:hover {
          color: ${props =>
            props.theme.colors.black};
          text-decoration-color: ${props =>
            props.theme.colors.black};
        }
      }
      &:hover {
        color: ${props =>
          props.isThemeReverse
            ? lighten(0.5, props.activeColor)
            : props.activeColor};
      }
    }

    strong {
      font-weight: ${props => props.theme.weight.xbase};
    }

    img {
      width: 100%;
      padding-bottom: ${props => props.theme.spacing.l};
      padding-top: ${props => props.theme.spacing.xl1};
      &.rdw-link-decorator-icon {
        width: auto;
        padding: 0;

      }
    }

    pre {
      background-color: #f6f8fa;
      border-radius: 3px;
      font-size: 85%;
      line-height: 1.45;
      overflow: auto;
      padding: 16px;
      padding-top: ${props => props.theme.spacing.s};
      padding-bottom: ${props => props.theme.spacing.m};
    }

    ${props =>
      !props.isProfile &&
      `blockquote {
        padding-bottom: ${props.theme.spacing.s};

      p, span  {
        padding-top: ${props.theme.spacing.s};
        padding-bottom: ${props.theme.spacing.s};
        font-size: ${props.theme.sizes.xl3m};
        color: #bdbdbd;
        line-height: 1.3;
        font-family: ${props.theme.type.serif};
            }
    }`}

    table {
      line-height: 1.6;
      padding: 0;
      padding-bottom: ${props => props.theme.spacing.s};
      text-transform: none;
      font-size: ${rem("15px")};
      font-weight: ${props => props.theme.weight.light};
      th {
        text-align: left !important;
        font-weight: ${props => props.theme.weight.xbase} !important;
      }

      td {
        text-align: left !important;
      }
    }

    .food-table {
      overflow-x: auto;
      table {
        margin: 2em 0;
        tr {
          th {
            padding: 0 10px 0 0;
            vertical-align: top;
            text-align: center !important;
            width: 90px;
          }
          th:nth-child(1) {
            width: auto;
            text-align: left !important;
          }
          td {
            padding-left: 0;
            padding-right: 10px;
            vertical-align: middle;
            text-align: center !important;
            :first-child {
              text-align: left !important;
            }
          }
        }
      }
    }
  }
`;

export const Header = ({ children, as, ...props }) => (
  <Title as={as} {...props}>
    {children}
  </Title>
);

Header.defaultProps = {
  as: "h1"
};

Header.propTypes = {
  as: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5"]),
  children: PropTypes.node.isRequired
};

export default MarkdownRenderer;
